// Package imports:
import React, { useEffect, useMemo, useState } from 'react';
import AOS from "aos";
import { SwiperSlide } from 'swiper/react';
// Component imports:
import PriceCard from '../PriceCard/PriceCard';
import Slider from '../Slider/Slider';
import Button from '../../ui-elements/Button/Button';
// import DropSelect from '../../ui-elements/DropSelect/DropSelect';
import BaseTable from '../../ui-elements/BaseTable/BaseTable';
import SubscriptionPricingTable from '../../ui-elements/SubscriptionPricingTable/SubscriptionPricingTable';
import Alert from '../../ui-elements/Alert/Alert';
import Loading from '../../ui-elements/Loading/Loading';
import HeadSection from '../../ui-elements/HeadSection/HeadSection';
// Service imports:
import { GET_KELDAN_API_URL } from '../../services/config';
import { ErrorMessages } from '../../services/errorMessages';
// Type imports:
import { IKeldanApiPricingViewModel } from '../../types/HomeTypes';
import { Fetched } from '../../types/Types';
// Static data imports:
import { pricingPageData } from '../../assets/staticData/pricingPlansTable';

const plansColumns = ['', 'Frítt', 'Áskrift', 'Sérsamningur']

const SubscriptionPage: React.FC = () => {

    const [ data, setData ] = useState<Fetched<IKeldanApiPricingViewModel>>(null);
    const [ isMobile, setIsMobile ] = useState(false);
    const [ visiblePlansColumns, setVisiblePlansColumns ] = useState(plansColumns);
    const [ visiblePlanIndex, setVisiblePlanIndex ] =  useState(1);
    AOS.init()

    const fetchData = async () => {
        try {
            const url = `${GET_KELDAN_API_URL()}/Home/GetPriceList`;
            const headers = {
                'Content-Type': 'application/json; charset=utf-8'
            };
            
            const response = await fetch(url, {
                method: 'GET',
                headers
            });
            if (response.ok) {
                const body = await response.json();
                setData(body);
            } else {
                setData(new Error(ErrorMessages.RequestFailed));
            }
        } catch (e) {
            
            setData(new Error(ErrorMessages.NetworkError));
        }
    }

    useEffect(() => {
        fetchData();
    }, []);

    const visiblePlansData = useMemo(() => {
        const { pricingPlansTable } = pricingPageData

        if(!isMobile) {
            setVisiblePlansColumns(plansColumns)
            return pricingPlansTable
        }
        
        const visibleTableData = pricingPlansTable.map(dataItem => {
            let tableColumnIndex = visiblePlanIndex + 1;

            if (visiblePlanIndex === 1) {
                tableColumnIndex = 2
            }

            if(visiblePlanIndex === 2) {
                tableColumnIndex = 3
            }

            setVisiblePlansColumns([
                plansColumns[0],
                plansColumns[tableColumnIndex]
            ])

            return [
                dataItem[0],
                dataItem[tableColumnIndex]
            ]
        });

        return visibleTableData;
    }, [isMobile, visiblePlanIndex]);

    const checkMobileLayout = () => {
        let mediaMatch = window.matchMedia('(max-width: 768px)');
        setIsMobile(mediaMatch.matches)
    }

    useEffect(() => {
        checkMobileLayout();
        
        window.addEventListener('resize', checkMobileLayout);

        return () => {
            window.removeEventListener('resize', checkMobileLayout)
        };
    }, [])

    return (
        <div className="main KCL_subscription-page">
            <div className="main__inner">
                <div className="section section--pricing shell">
                    <HeadSection
                        title='Áskriftarleiðir'
                        description={
                            <p>Keldan veitir þér aðgang að öllum helstu skrám sem reknar eru af opinberum aðilum á Íslandi. Athugið að keyptar skýrslur eru samkvæmt verðskrá. <br/>
                                Veldu þá leið sem hentar þér eða þínu fyrirtæki.</p>
                        }
                        textAlign='center'
                    />
                    <div className="section__body">
                        <div className="section__inner">
                            <div 
                                className="section__list"
                                data-aos="fade-in"
                                data-aos-delay="50"
                            >
                                <div className="subscription-plans">
                                    <div className="subscription-plans__slider">
                                        <Slider
                                            perView={3}
                                            centerSlides={true}
                                            loop={false}
                                            hideActions={true}
                                            initialSlide={1}
                                            breakpointOptions={{
                                                320: {
                                                    slidesPerView: 1,
                                                    noSwiping: false
                                                },
                                                768: {
                                                    slidesPerView: 3,
                                                    noSwiping: true
                                                }
                                            }}
                                            onSlideChange={(index) => setVisiblePlanIndex(index)}
                                            onInit={() => {}}
                                            className="price-cards__slider swiper-no-swiping"
                                        >
                                            <SwiperSlide>
                                                <PriceCard
                                                    variant="tertiary"
                                                    type="Frítt"
                                                    price="0"
                                                    priceInfo="kr. á mánuði "
                                                    key="Frítt"
                                                >
                                                    <div className="price-card__info">
                                                        <p>Fyrir þá sem vilja skoða gögn og kaupa einstakar skýrslur.</p>
                                                    </div>

                                                    <div className="price-card__actions">
                                                        <Button anchorProps={{href: "/Nyskraning"}}>
                                                            Stofna aðgang
                                                        </Button>
                                                    </div>
                                                </PriceCard>
                                            </SwiperSlide>
                                            <SwiperSlide>
                                                <PriceCard
                                                    variant="primary"
                                                    type="Áskrift"
                                                    price='4.900'
                                                    priceInfo="kr. á mánuði + VSK"
                                                    key="Áskrift"
                                                    showRibbon
                                                >                            
                                                    <div className="price-card__info">
                                                        <p>Fyrir þá sem vilja skoða gögn, vakta fyrirtæki og kaupa ódýrari skýrslur.<br/> Fyrir allt að 4 notendur.</p>
                                                    </div>
                            
                                                    <div className="price-card__actions">
                                                        <Button buttonType='buy' anchorProps={{
                                                            href: `/Askrift`
                                                            }}
                                                        >
                                                            Kaupa áskrift
                                                        </Button>
                                                    </div>
                                                </PriceCard>
                                            </SwiperSlide>
                                            <SwiperSlide>
                                                <PriceCard
                                                    variant="secondary"
                                                    type="Sérsamningur"
                                                    priceText="Umsamið"
                                                    key="Sérsamningur"
                                                >
                                                    <div className="price-card__info">
                                                        <p>Fyrir kröfuharða notendur sem þurfa aðgang fyrir fimm eða fleiri í sínu fyrirtæki.</p>
                                                    </div>

                                                    <div className="price-card__actions">
                                                        <Button anchorProps={{href: "/Hafa-Samband"}}>
                                                            Hafðu samband
                                                        </Button>
                                                    </div>
                                                </PriceCard>
                                            </SwiperSlide>
                                        </Slider>
                                    </div>
                                </div>
                            </div>
                            <div 
                                className="section__group"
                                data-aos="fade-in"
                                data-aos-delay="50"
                            >
                                <BaseTable 
                                    columns={visiblePlansColumns}
                                    rows={visiblePlansData}
                                    className="subscription-plans-table"
                                />
                            </div>
                            <div 
                                className="section__group"
                                data-aos="fade-in"
                                data-aos-delay="50"
                                id="verdskra"
                            >
                                <h2 id="Pricing_PriceTable" className="h2">Verðskrá Keldunnar</h2>
                                <p><em>Öll verð eru án vsk.</em></p>

                                {(data === null)
                                ? <Loading />
                                : (data instanceof Error)
                                ? <Alert type='error' headText={data.message} />
                                : (
                                    <SubscriptionPricingTable 
                                        groups={data}
                                    />
                                )}
                                    
                                <div className="section__footnote">
                                    <p>Allar skýrslur sem innskráður notandi kaupir eru aðgengilegar á síðunni <strong>Keyptar skýrslur</strong> án frekari kostnaðar.</p>
                                    <p>Þar er einnig að finna nákvæmt yfirlit yfir allan kostnað vegna kaupa á skýrslum.</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default SubscriptionPage;
